/* eslint-disable max-lines-per-function, max-statements */
import { useMutation, useQuery } from "@apollo/client";
import prepareComponent from "@bluframe/grapple/prepareComponent";
import { mutations, queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import { serviceNameLabels } from "@bluframe/mightychroma-mightytool-ui";
import { newsServiceNames } from "config/site/serviceNames";
import useUser from "hooks/useUser";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import NewsComponent from "./News";
const { news: { GENERATE_DRAFT_NEWS, PUBLISH_DRAFT_NEWS } } = mutations;
const { news: { GET_DRAFTS_NEWS_WAVE } } = queries;
const usePrepareComponent = () => {
    const history = useHistory();
    const { search } = useLocation();
    const startAt = new URLSearchParams(search).get("startAt");
    const { isAdmin, isEditor } = useUser();
    const { data, loading } = useQuery(GET_DRAFTS_NEWS_WAVE, {
        fetchPolicy: "network-only",
        variables: {
            cursor: startAt ?? undefined
        }
    });
    const [generateDrafts, { loading: generateDraftsLoading }] = useMutation(GENERATE_DRAFT_NEWS, {
        refetchQueries: [GET_DRAFTS_NEWS_WAVE]
    });
    const [publishDrafts, { loading: publishDraftsLoading }] = useMutation(PUBLISH_DRAFT_NEWS, {
        refetchQueries: [GET_DRAFTS_NEWS_WAVE]
    });
    const onView = useCallback((id) => {
        history.push(`/news/${id}`);
    }, [history]);
    const onGenerate = useCallback((id, overrides) => {
        generateDrafts({
            variables: {
                generateDraftsInputs: [
                    { additionalInstructions: overrides?.additionalInstructions, id }
                ],
                options: { isAddToQueue: true }
            }
        });
    }, [generateDrafts]);
    const onPublish = useCallback((id, overrides) => {
        publishDrafts({
            variables: {
                // Preserve editor-approved copy during publish. We only need to enqueue the publish job.
                options: { isAddToQueue: true },
                publishDraftsInputs: [
                    { additionalInstructions: overrides?.additionalInstructions, id }
                ]
            }
        });
    }, [publishDrafts]);
    /**
     * getPreviewsFromDraft
     *
     * @description
     * Extracts preview information from a News draft, prioritizing known service names
     * @param {News} draft - The News draft object
     * @returns {DraftPreview[]} Array of DraftPreview objects
     *
     */
    const getPreviewsFromDraft = (draft) => {
        const previews = [];
        /**
         * Add known services URLs first
         */
        newsServiceNames.forEach((serviceName) => {
            const review = draft.sources.find((item) => item.serviceName === serviceName);
            if (review) {
                previews.push({
                    name: serviceNameLabels[serviceName],
                    url: review.url ?? ""
                });
            }
        });
        /**
         * Add any additional URLs that aren't associated with a known service
         */
        draft.sources.forEach((source) => {
            if (!source.serviceName &&
                source.url &&
                !previews.find((preview) => preview.url === source.url)) {
                previews.push({
                    name: source.url,
                    url: source.url
                });
            }
        });
        return previews;
    };
    const drafts = useMemo(() => data?.draftsNewsWave.news
        .filter((draft) => Boolean(draft.name))
        .map((draft) => ({
        homeVideo: draft.homeVideo ?? undefined,
        id: draft.id,
        image: draft.images?.[0],
        isChecked: false,
        isGenerateLoading: generateDraftsLoading,
        isPublishLoading: publishDraftsLoading,
        // eslint-disable-next-line no-empty-function
        onChange: () => { },
        onGenerate,
        onPublish,
        onView,
        previews: getPreviewsFromDraft(draft),
        // Typescript needs the `as string` here to satisfy the type checker
        // we filter out drafts without a name or title above
        title: draft.name
    })), [
        data?.draftsNewsWave.news,
        generateDraftsLoading,
        onGenerate,
        onPublish,
        onView,
        publishDraftsLoading
    ]);
    return {
        drafts,
        isAdmin,
        isEditor,
        loading,
        pagination: {
            hasNextData: data?.draftsNewsWave.hasNextData,
            hasPrevData: data?.draftsNewsWave.hasPrevData,
            link: "/drafts/news",
            next: data?.draftsNewsWave.cursor,
            prev: data?.draftsNewsWave.prevCursor
        }
    };
};
export default prepareComponent(usePrepareComponent)(NewsComponent);
